import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import type ComponentTypes from "~/features/cms/componentTypes";

export interface CMSPageNavigationElementProps {
  label: string;
  href: string;
}

export interface CMSPageNavigationComponentProps {
  className?: string;
  type: ComponentTypes.CMSPageNavigation;
  items: CMSPageNavigationElementProps[]; // not `elements`
}

export const CMSPageNavigation = ({ items, className }: CMSPageNavigationComponentProps) => {
  // const { activeIndex, triggerSetActiveIndex } = usePageNavigation(items);
  const navigationRef = useRef<HTMLDivElement>(null);

  const navigationHeight = navigationRef.current?.getBoundingClientRect().height ?? 0;
  const { activeIndex } = usePageNavigation(items, navigationHeight);

  useEffect(() => {
    const el = navigationRef.current;
    if (el && el.parentElement) {
      el.parentElement.classList.add("scha__cmspagenav__parent-container");
      return () => el.parentElement?.classList.remove("scha__cmspagenav__parent-container");
    }
  }, [navigationRef.current]);

  useEffect(() => {
    if (!window.location.hash) {
      return;
    }

    const element = document.querySelector(window.location.hash);
    if (element && navigationRef.current) {
      const navHeight = navigationRef.current.getBoundingClientRect().height;
      const elementOffset = getElementPageOffsetTop(element as HTMLElement);
      window.scrollTo({ top: elementOffset - navHeight });
    }
  }, [navigationRef.current]);

  if (!items?.length) {
    return null;
  }

  const handleNavigationClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const element = document.querySelector(e.currentTarget.getAttribute("href") ?? "");
    if (element && navigationRef.current) {
      if (element.id) {
        window.location.hash = element.id;
      }
      const navHeight = navigationRef.current.getBoundingClientRect().height;
      const elementOffset = getElementPageOffsetTop(element as HTMLElement);
      window.scrollTo({ top: elementOffset - navHeight, behavior: "smooth" });
    }
  };

  return (
    <div className={classNames("scha__cmspagenav", className)} ref={navigationRef}>
      <ul className="scha__cmspagenav__elements">
        {items.map((element, index) => (
          <li
            key={`scha__cmspagenav-${index}`}
            id={`nav-${encodeURIComponent(element.href.replace("#", ""))}`}
            className={classNames("scha__cmspagenav__element", { active: index === activeIndex })}
          >
            <a
              href={element.href}
              className={classNames("scha__cmspagenav__link", { active: index === activeIndex })}
              // onClick={() => triggerSetActiveIndex(index)}
              onClick={handleNavigationClick}
            >
              <span className="scha__cmspagenav__label">{element.label}</span>
            </a>
          </li>
        ))}
      </ul>
      <hr className="scha__cmspagenav__line" />
    </div>
  );
};

const EPSILON_OFFSET = 2;

export function usePageNavigation(items: CMSPageNavigationElementProps[], viewportOffset = 0) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      // Get all section elements
      const sectionElements = items.map((entry) => document.querySelector(entry.href)).filter(Boolean) as HTMLElement[];
      if (sectionElements.length === 0) {
        return;
      }

      let newActiveIndex: number = activeIndex;
      const viewportTop = viewportOffset + EPSILON_OFFSET;

      let minDist = Infinity;
      for (let i = 0; i < sectionElements.length; i++) {
        const el = sectionElements[i];
        const rect = el.getBoundingClientRect();
        // Only consider elements whose top is <= 0 (in or above viewport top)
        if (rect.top <= viewportTop) {
          const dist = Math.abs(rect.top - viewportTop);
          if (dist < minDist) {
            minDist = dist;
            newActiveIndex = i;
          }
        }
      }

      if (newActiveIndex !== activeIndex) {
        setActiveIndex(newActiveIndex);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    // Run once on mount
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [items, activeIndex, viewportOffset]);

  return {
    activeIndex,
  };
}

function getElementPageOffsetTop(element: HTMLElement): number {
  let offsetTop = 0;
  let current: HTMLElement | null = element;

  while (current) {
    offsetTop += current.offsetTop;
    current = current.offsetParent as HTMLElement | null;
  }

  return offsetTop;
}
